import React, { useMemo } from "react";
import { useNavigate, Link } from "react-router";
import {
  Box,
  Typography,
  Button,
  CardMedia,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getAddress, getEventColour } from "../utils/data"; // Adjust the import path as necessary
import moment from "moment";

function EventInfo({ event, showBackButton = true }) {
  const navigate = useNavigate();

  const scheme = useMemo(() => {
    switch (event.siteInfo.siteID.substr(0, 3)) {
      case "038":
        return "cranleigh";
      default:
        return "loyaltypro";
    }
  }, [event.siteInfo.siteID]);

  const isMultiDay = !moment(event.eventDate).isSame(event.endDate, "day");

  if (!event)
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6">Event not found.</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );

  return (
    <Box sx={{ maxWidth: {xs: 700, md: 1000, lg: 1200 }, margin: "0 auto", p: { xs: 2, sm: 3, md: 4 } }}>
      {/* Back Button */}
      {showBackButton && (
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2, color: "text.secondary" }}
        >
          Back to Events
        </Button>
      )}

      {/* Hero Image */}
      {event.imageURL && (
        <CardMedia
          component="img"
          sx={{
            height: { xs: 200, sm: 300 },
            width: "auto",
            maxWidth: {xs: "100%", sm: "none"},
            borderRadius: 3,
            mb: 3,
            objectFit: "contain",
            boxShadow: 3,
            marginX: "auto",
          }}
          image={event.imageURL}
          alt={event.title}
        />
      )}

      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Chip
          label={event.eventType}
          size="small"
          sx={{
            backgroundColor: getEventColour(event.eventType),
            color: "#fff",
            fontWeight: "bold",
            mb: 1.5,
            borderRadius: 1.5,
          }}
        />
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.75rem", sm: "2.5rem" },
            lineHeight: 1.2,
          }}
        >
          {event.title}
        </Typography>
      </Box>

      {/* Details Section (Date & Location) */}
      <Stack spacing={2} sx={{ mb: 4, color: "text.secondary" }}>
        {/* Date/Time */}
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <CalendarTodayIcon sx={{ mt: 0.3, color: "primary.main" }} />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {isMultiDay
              ? `${moment(event.eventDate).format("MMM Do")} – ${moment(event.endDate).format("MMM Do YYYY")}\n${moment(event.eventDate).format("h:mm A")} – ${moment(event.endDate).format("h:mm A")}`
              : `${moment(event.eventDate).format("MMMM Do YYYY")}\n${moment(event.eventDate).format("h:mm A")} – ${moment(event.endDate).format("h:mm A")}`}
          </Typography>
        </Stack>

        {/* Location */}
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <PlaceIcon sx={{ mt: 0.3, color: "primary.main" }} />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {getAddress({ siteInfo: event.siteInfo })}
          </Typography>
        </Stack>
      </Stack>

      {/* Action Buttons */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 4 }}>
        {event.eventUrl && (
          <Button
            component={Link}
            to={event.eventUrl}
            target="_blank"
            variant="contained"
            size="large"
            fullWidth
            sx={{ borderRadius: 2, fontWeight: "bold" }}
          >
            Event Website
          </Button>
        )}

        <Button
          component={Link}
          to={`${scheme}://home/site/${event.siteInfo.siteID}`}
          variant="outlined"
          size="large"
          fullWidth
          sx={{
            borderRadius: 2,
            borderWidth: 2,
            "&:hover": { borderWidth: 2 },
          }}
        >
          Venue Information
        </Button>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {/* Description */}
      <Typography
        variant="body1"
        sx={{
          fontSize: "1.1rem",
          lineHeight: 1.7,
          whiteSpace: "pre-line",
          color: "text.primary",
        }}
      >
        {event.description}
      </Typography>
    </Box>
  );
}

export default EventInfo;
